import type { MouseEvent, PointerEvent, ReactElement, ReactNode } from "react";
import {
  footprint,
  getItemDef,
  gridCapacity,
  usedCells,
  type AreaId,
  type GridRect,
  type GridState,
  type PlacedItem,
} from "@rietz/shared";
import { TEXT_DIM } from "../theme.js";
import { CELL, placedSize, tileDropOutline, type DragState, type ItemSource } from "./drag.js";
import { TileBody } from "./tile.js";

/**
 * One grid of Tetris. The lattice is a background gradient rather than w×h
 * divs; items are absolutely positioned over it. The `data-drop-area` element
 * is EXACTLY w*CELL by h*CELL with no border — `zoneAt` turns its rect straight
 * into cells. ONE component draws every grid, the open box's included: a nested
 * grid is addressed by its host's iid (`data-drop-area="inside"` plus
 * `data-box-iid`) and its tiles carry a `"box"` source.
 */

export const GRID_CELL_BACKGROUND =
  "linear-gradient(to right, rgba(255, 255, 255, 0.055) 1px, transparent 1px)," +
  "linear-gradient(to bottom, rgba(255, 255, 255, 0.055) 1px, transparent 1px)";

/**
 * Pixels of daylight around each pouch of a divided container, so four 1×2
 * pockets do not read as one 4×2 tray. Purely paint — the drop layer still
 * reads cells off the parent's rect.
 */
export const POUCH_GUTTER = 3;

type PxBox = { left: number; top: number; width: number; height: number };

/** A cell rect as pixels, pulled in by the gutter when the grid has pouches. */
function pxBox(cells: { x: number; y: number; w: number; h: number }, pouched: boolean): PxBox {
  const g = pouched ? POUCH_GUTTER : 0;
  return {
    left: cells.x * CELL + g,
    top: cells.y * CELL + g,
    width: cells.w * CELL - 2 * g,
    height: cells.h * CELL - 2 * g,
  };
}

/**
 * WHICH grid this is, as the drop layer addresses it: a top-level area, or the
 * inside of one container. A union so a nested grid cannot be rendered without
 * its host's id.
 */
export type GridPanelTarget =
  | { area: AreaId; boxIid?: undefined }
  | { area: "inside"; boxIid: string };

export type GridPanelProps = GridPanelTarget & {
  /** English caption, or "" for a panel whose header already names it. */
  title: string;
  grid: GridState;
  drag: DragState | null;
  onTilePointerDown: (ev: PointerEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileContextMenu: (ev: MouseEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileDoubleClick: (ev: MouseEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  /** Pointer entered or moved within a tile — the tooltip's dwell timer. */
  onTileHover: (ev: PointerEvent<HTMLDivElement>, placed: PlacedItem, from: ItemSource) => void;
  onTileLeave: () => void;
  /**
   * Items not yet found by the container search: drawn as "?" and — the part
   * that matters — with NONE of the pointer handlers, so no drag, menu, tooltip
   * or Ctrl+click. `undefined` when no search is engaged.
   */
  hiddenIids?: ReadonlySet<string>;
  /** The item the search lens is currently over, or `null`. */
  searchFocusIid?: string | null;
  /**
   * Per-tile paint the host screen wants on top of the item — the trader's
   * price, dim and ring. `undefined` changes nothing.
   */
  decorateTile?: (placed: PlacedItem) => {
    footer?: ReactNode;
    dimmed?: boolean;
    marked?: boolean;
  };
};

export function GridPanel(props: GridPanelProps): ReactElement {
  const { grid, drag } = props;
  const area = props.area;
  const boxIid = props.boxIid;
  const source: ItemSource =
    props.boxIid === undefined
      ? { t: "grid", area: props.area }
      : { t: "box", boxIid: props.boxIid };

  return (
    <div>
      {props.title === "" ? null : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 4,
            color: TEXT_DIM,
            fontSize: 11,
            letterSpacing: "0.04em",
          }}
        >
          <span>{props.title}</span>
          <span>
            {usedCells(grid)}/{gridCapacity(grid)}
          </span>
        </div>
      )}

      <div
        style={{
          display: "inline-block",
          border: "1px solid rgba(214, 205, 194, 0.22)",
          borderRadius: 3,
          background: "rgba(0, 0, 0, 0.34)",
        }}
      >
        <div
          data-drop-area={area}
          data-box-iid={boxIid}
          style={{
            position: "relative",
            width: grid.w * CELL,
            height: grid.h * CELL,
            // A POUCHED grid paints no lattice of its own: the cells between
            // pouches
            // are dead. The ELEMENT is unchanged — still w*CELL by h*CELL, no
            // border.
            backgroundImage: grid.zones === undefined ? GRID_CELL_BACKGROUND : undefined,
            backgroundSize: `${CELL}px ${CELL}px`,
            // The highlight of an item hanging off the edge is clipped rather
            // than allowed to paint over the neighbouring panel.
            overflow: "hidden",
          }}
        >
          {grid.zones?.map((zone) => (
            <PouchOutline key={`${zone.x},${zone.y}`} zone={zone} />
          ))}

          <Highlight grid={grid} area={area} boxIid={boxIid} drag={drag} />

          {grid.items.map((placed) => {
            const def = getItemDef(placed.item.defId);
            const f = placedSize(placed);
            const box = {
              position: "absolute" as const,
              ...pxBox({ x: placed.x, y: placed.y, w: f.w, h: f.h }, grid.zones !== undefined),
            };

            // Not a variant of the tile below but a different element: no
            // handlers at all, and nothing about the item on it.
            if (props.hiddenIids?.has(placed.item.iid) === true) {
              return (
                <div key={placed.item.iid} style={box}>
                  <HiddenTile />
                </div>
              );
            }

            const deco = props.decorateTile?.(placed);
            const body =
              def === null ? (
                <UnknownTile />
              ) : (
                <TileBody
                  def={def}
                  item={placed.item}
                  rot={placed.rot}
                  faded={drag !== null && drag.iid === placed.item.iid}
                  dimmed={deco?.dimmed}
                  marked={deco?.marked}
                  footer={deco?.footer}
                  // A part going onto this gun, or rounds going into this gun
                  // or this magazine — one ring, both gestures.
                  modDrop={tileDropOutline(drag, placed.item.iid)}
                  containerDrop={
                    drag?.hover?.t === "container" &&
                    drag.hover.containerIid === placed.item.iid
                      ? drag.hover.ok
                        ? "valid"
                        : "invalid"
                      : undefined
                  }
                />
              );

            return (
              <div
                key={placed.item.iid}
                onPointerDown={(ev) => {
                  props.onTilePointerDown(ev, placed, source);
                }}
                onContextMenu={(ev) => {
                  props.onTileContextMenu(ev, placed, source);
                }}
                onDoubleClick={(ev) => {
                  props.onTileDoubleClick(ev, placed, source);
                }}
                // Enter AND move: moving re-arms the tooltip after a drag or
                // menu
                // dismissed one.
                onPointerEnter={(ev) => {
                  props.onTileHover(ev, placed, source);
                }}
                onPointerMove={(ev) => {
                  props.onTileHover(ev, placed, source);
                }}
                onPointerLeave={props.onTileLeave}
                style={box}
              >
                {/* While a search is running the tile is re-keyed on the
                    hidden → revealed flip, so React remounts it and the
                    one-shot reveal animation actually plays. Outside a search
                    there is no wrapper at all and the markup is unchanged. */}
                {props.hiddenIids === undefined ? (
                  body
                ) : (
                  <div key="v" className="invsearch-reveal" style={{ position: "absolute", inset: 0 }}>
                    {body}
                  </div>
                )}
              </div>
            );
          })}

          <SearchLens grid={grid} focusIid={props.searchFocusIid ?? null} />
        </div>
      </div>
    </div>
  );
}

/**
 * One pouch of a divided container, drawn under everything else. Purely
 * decoration — `pointerEvents: "none"`, no data attributes — since the drop
 * layer works in CELLS off the parent's rect.
 */
function PouchOutline(props: { zone: GridRect }): ReactElement {
  const { zone } = props;
  return (
    <div
      style={{
        position: "absolute",
        ...pxBox(zone, true),
        boxSizing: "border-box",
        border: "1px solid rgba(214, 205, 194, 0.28)",
        borderRadius: 3,
        // `backgroundColor`, not the `background` shorthand, which would reset
        // the lattice image.
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        backgroundImage: GRID_CELL_BACKGROUND,
        backgroundSize: `${CELL}px ${CELL}px`,
        // The outline starts a gutter in from the cell corner; slide the
        // lattice back out so its lines still fall on the cell boundaries.
        backgroundPosition: `${-POUCH_GUTTER}px ${-POUCH_GUTTER}px`,
        pointerEvents: "none",
      }}
    />
  );
}

/**
 * The magnifier gliding from item to item during a search: one element
 * positioned by the focused item's centre and left to CSS to travel.
 */
function SearchLens(props: { grid: GridState; focusIid: string | null }): ReactElement | null {
  if (props.focusIid === null) return null;
  const placed = props.grid.items.find((p) => p.item.iid === props.focusIid);
  if (placed === undefined) return null;
  const f = placedSize(placed);
  const size = CELL * 1.3;
  return (
    <div
      className="invsearch-lens"
      style={{
        position: "absolute",
        left: (placed.x + f.w / 2) * CELL - size / 2,
        top: (placed.y + f.h / 2) * CELL - size / 2,
        width: size,
        height: size,
        pointerEvents: "none",
        zIndex: 3,
      }}
    />
  );
}

/** An item the player has not found yet. Says nothing about what it is. */
function HiddenTile(): ReactElement {
  return (
    <div
      className="invsearch-hidden"
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: 3,
        border: "1px dashed rgba(220, 210, 200, 0.35)",
        background: "rgba(70, 72, 76, 0.45)",
        color: TEXT_DIM,
        font: "600 14px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      ?
    </div>
  );
}

/**
 * Where the dragged item would land, green or red. A merge highlights the
 * TARGET stack, not the footprint — a different statement.
 */
function Highlight(props: {
  grid: GridState;
  area: AreaId | "inside";
  /** Set for a nested grid. Two boxes can both be `"inside"`, so the id is the
   *  half of the address that actually tells them apart. */
  boxIid: string | undefined;
  drag: DragState | null;
}): ReactElement | null {
  const { drag, grid } = props;
  if (drag === null || drag.hover === null) return null;
  if (drag.hover.t !== "cell" || drag.hover.area !== props.area) return null;
  if (drag.hover.boxIid !== props.boxIid) return null;
  const def = getItemDef(drag.defId);
  if (def === null) return null;

  const hover = drag.hover;
  let box = { x: hover.x, y: hover.y, ...footprint(def, drag.rot) };
  if (hover.ontoIid !== null) {
    const onto = grid.items.find((p) => p.item.iid === hover.ontoIid);
    if (onto !== undefined) box = { x: onto.x, y: onto.y, ...placedSize(onto) };
  }

  return (
    <div
      style={{
        position: "absolute",
        ...pxBox(box, grid.zones !== undefined),
        boxSizing: "border-box",
        borderRadius: 3,
        background: hover.ok ? "rgba(112, 190, 120, 0.26)" : "rgba(212, 78, 68, 0.26)",
        border: `1px solid ${hover.ok ? "rgba(140, 220, 148, 0.75)" : "rgba(232, 104, 92, 0.8)"}`,
        pointerEvents: "none",
      }}
    />
  );
}

/** A def the client has never heard of. One cell, and honest about it. */
function UnknownTile(): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: 3,
        border: "1px dashed rgba(220, 210, 200, 0.4)",
        background: "rgba(80, 80, 84, 0.3)",
        color: TEXT_DIM,
        fontSize: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      ?
    </div>
  );
}
